<template>
  <div class="pp-page">
    <div class="pp-card">
      <div class="pp-icon">
        <i class="mdi mdi-shield-key"></i>
      </div>
      <h1 class="pp-title">Change your password</h1>
      <p class="pp-subtitle">
        For your security, please set a new password before continuing.
      </p>

      <label class="pp-label">Current (temporary) password</label>
      <div class="pp-input-wrap">
        <i class="mdi mdi-lock-outline pp-input-icon"></i>
        <input v-model="oldPassword" :type="showOld?'text':'password'" class="pp-input pp-input-pass" autofocus />
        <button type="button" class="pp-eye" @click="showOld = !showOld" :title="showOld?'Hide':'Show'">
          <i class="mdi" :class="showOld ? 'mdi-eye-off' : 'mdi-eye'"></i>
        </button>
      </div>

      <label class="pp-label mt-3">New password</label>
      <div class="pp-input-wrap">
        <i class="mdi mdi-lock pp-input-icon"></i>
        <input v-model="newPassword" :type="showNew?'text':'password'" class="pp-input pp-input-pass" />
        <button type="button" class="pp-eye" @click="showNew = !showNew" :title="showNew?'Hide':'Show'">
          <i class="mdi" :class="showNew ? 'mdi-eye-off' : 'mdi-eye'"></i>
        </button>
      </div>

      <label class="pp-label mt-3">Confirm new password</label>
      <div class="pp-input-wrap">
        <i class="mdi mdi-lock-check pp-input-icon"></i>
        <input v-model="confirmPassword" :type="showNew?'text':'password'" class="pp-input pp-input-pass" @keyup.enter="submit" />
      </div>

      <ul class="pp-rules">
        <li :class="ruleLen ? 'ok' : ''"><i class="mdi" :class="ruleLen ? 'mdi-check-circle' : 'mdi-circle-outline'"></i> At least 8 characters</li>
        <li :class="ruleDiff ? 'ok' : ''"><i class="mdi" :class="ruleDiff ? 'mdi-check-circle' : 'mdi-circle-outline'"></i> Different from current password</li>
        <li :class="ruleMatch ? 'ok' : ''"><i class="mdi" :class="ruleMatch ? 'mdi-check-circle' : 'mdi-circle-outline'"></i> New password and confirmation match</li>
      </ul>

      <button class="pp-btn pp-btn-primary" :disabled="!canSubmit || submitting" @click="submit">
        {{ submitting ? 'Saving…' : 'Update password' }}
      </button>

      <div v-if="error"   class="pp-error">{{ error }}</div>
      <div v-if="success" class="pp-success">{{ success }}</div>

      <div class="pp-divider"></div>

      <div class="pp-footer">
        <a href="javascript:void(0)" @click="signOut" class="pp-link">
          <i class="mdi mdi-logout"></i> Sign out
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { execute2, hms } from "@/api";

export default {
  name: "PatientChangePassword",
  data() {
    return {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
      showOld: false,
      showNew: false,
      submitting: false,
      error: "",
      success: ""
    };
  },
  computed: {
    ruleLen()   { return this.newPassword.length >= 8; },
    ruleDiff()  { return this.newPassword.length > 0 && this.newPassword !== this.oldPassword; },
    ruleMatch() { return this.newPassword.length > 0 && this.newPassword === this.confirmPassword; },
    canSubmit() {
      return this.oldPassword && this.ruleLen && this.ruleDiff && this.ruleMatch;
    }
  },
  methods: {
    async submit() {
      this.error = ""; this.success = "";
      const token = localStorage.getItem('patientPortalToken');
      if (!token) { this.$router.push('/patient-portal/login'); return; }
      this.submitting = true;
      try {
        const r = await execute2({
          function: 'patientPortalChangePassword',
          token,
          old_password: this.oldPassword,
          new_password: this.newPassword
        }, hms);
        if (r.data && r.data.success) {
          localStorage.removeItem('patientPortalMustChangePassword');
          this.success = "Password updated. Taking you to your portal…";
          setTimeout(() => this.$router.push('/portal'), 900);
        } else {
          this.error = r.data?.message || 'Could not update password.';
        }
      } catch (e) {
        console.error(e);
        this.error = 'Network error.';
      } finally {
        this.submitting = false;
      }
    },
    signOut() {
      localStorage.removeItem('patientPortalToken');
      localStorage.removeItem('patientPortalUser');
      this.$router.push('/patient-portal/login');
    }
  }
};
</script>

<style scoped>
.pp-page { min-height: 100vh; background: #f5f6f8; display: flex; align-items: center; justify-content: center; padding: 30px 20px; }

.pp-card { background: #fff; border-radius: 14px; padding: 36px 36px 28px; max-width: 460px; width: 100%; box-shadow: 0 4px 14px rgba(15, 23, 42, .06); text-align: center; }

.pp-icon { width: 80px; height: 80px; margin: 0 auto 18px; background: linear-gradient(135deg, #2c5fd1 0%, #1e3a8a 100%); border-radius: 16px; display: flex; align-items: center; justify-content: center; box-shadow: 0 6px 18px rgba(30, 58, 138, 0.25); }
.pp-icon i { font-size: 44px; color: #fff; }

.pp-title { font-size: 26px; font-weight: 800; color: #0f172a; margin: 0 0 6px; }
.pp-subtitle { color: #64748b; font-size: 14px; margin-bottom: 22px; line-height: 1.55; }

.pp-label { font-size: 13px; font-weight: 600; color: #334155; display: block; margin-bottom: 6px; text-align: left; }

.pp-input-wrap { position: relative; }
.pp-input { width: 100%; padding: 12px 14px 12px 42px; border: 1px solid #cbd5e1; background: #eef2ff; border-radius: 10px; font-size: 15px; color: #0f172a; transition: .15s; box-sizing: border-box; }
.pp-input:focus { outline: none; border-color: #2c5fd1; background: #fff; box-shadow: 0 0 0 3px rgba(44, 95, 209, 0.12); }
.pp-input-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #64748b; font-size: 18px; pointer-events: none; }
.pp-input-pass { padding-right: 42px; }
.pp-eye { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: #64748b; padding: 4px 8px; }
.pp-eye i { font-size: 18px; }

.pp-rules { list-style: none; padding: 0; margin: 14px 0 4px; text-align: left; font-size: 13px; color: #64748b; }
.pp-rules li { display: flex; align-items: center; gap: 8px; padding: 4px 0; }
.pp-rules li i { font-size: 18px; color: #cbd5e1; }
.pp-rules li.ok { color: #166534; }
.pp-rules li.ok i { color: #16a34a; }

.pp-btn { width: 100%; padding: 13px 16px; border-radius: 10px; border: none; font-weight: 700; font-size: 15px; cursor: pointer; margin-top: 22px; transition: .15s; }
.pp-btn-primary { background: #6c8eff; color: #fff; }
.pp-btn-primary:hover { background: #4f73e8; }
.pp-btn-primary:disabled { opacity: .55; cursor: not-allowed; }

.pp-error   { background: #fee2e2; color: #991b1b; padding: 10px 12px; border-radius: 8px; margin-top: 14px; font-size: 13px; text-align: left; }
.pp-success { background: #dcfce7; color: #166534; padding: 10px 12px; border-radius: 8px; margin-top: 14px; font-size: 13px; text-align: left; }

.pp-divider { height: 1px; background: #e2e8f0; margin: 22px 0 16px; }

.pp-footer { display: flex; justify-content: center; align-items: center; gap: 10px; font-size: 14px; }
.pp-link { color: #2c5fd1; text-decoration: none; font-weight: 500; }
.pp-link:hover { text-decoration: underline; }
.mt-3 { margin-top: 14px !important; }
</style>
